import type { CSSProperties } from 'react';

export interface PositionStyle {
  position: string;
  top: string | number;
  right: string | number;
  bottom: string | number;
  left: string | number;
  zIndex: string | number;
  positionTopActive: boolean;
  positionRightActive: boolean;
  positionBottomActive: boolean;
  positionLeftActive: boolean;
  centerHorizontally: boolean;
  centerVertically: boolean;
  prefix?: string;
}

const withPrefix = (key: string, prefix?: string) => {
  const keyWithoutPrefix = key.replace(/^--/, '');
  return prefix ? `--${prefix}-${keyWithoutPrefix}` : `--${keyWithoutPrefix}`;
};

export const generatePositionStyle = ({
  position = 'static',
  top,
  right,
  bottom,
  left,
  zIndex,
  positionTopActive,
  positionRightActive,
  positionBottomActive,
  positionLeftActive,
  centerHorizontally,
  centerVertically,
  prefix = '',
}: PositionStyle): CSSProperties => {
  const styles: CSSProperties = {
    [withPrefix('--position-top', prefix)]: 'initial',
    [withPrefix('--position-right', prefix)]: 'initial',
    [withPrefix('--position-left', prefix)]: 'initial',
    [withPrefix('--position-bottom', prefix)]: 'initial',
    [withPrefix('--position-transform', prefix)]: 'initial',
  } as CSSProperties;

  if (position === 'static') {
    styles[withPrefix('--position', prefix)] = 'static';
    return styles;
  }

  if (position === 'relative') {
    styles[withPrefix('--position', prefix)] = 'relative';
    styles[withPrefix('--z-index', prefix)] = zIndex;
    return styles;
  }

  styles[withPrefix('--position', prefix)] = 'absolute';
  styles[withPrefix('--z-index', prefix)] = zIndex;

  if (centerHorizontally && centerVertically) {
    styles[withPrefix('--position-top', prefix)] = '50%';
    styles[withPrefix('--position-left', prefix)] = '50%';
    styles[withPrefix('--position-transform', prefix)] = 'translate(-50%, -50%)';
    return styles; // If both are true, we don't need to set the top and bottom or left and right
  }

  if (positionTopActive) {
    styles[withPrefix('--position-top', prefix)] = top;
  }

  if (positionRightActive) {
    styles[withPrefix('--position-right', prefix)] = right;
  }

  if (positionBottomActive) {
    styles[withPrefix('--position-bottom', prefix)] = bottom;
  }

  if (positionLeftActive) {
    styles[withPrefix('--position-left', prefix)] = left;
  }

  return styles;
};
